import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { paymentsAPI } from '../lib/api';

const UpgradeSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const res = await paymentsAPI.getStatus(sessionId);
      setPaymentDetails(res.data);

      if (res.data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (res.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="upgrade-loading">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-teal-700 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4" data-testid="upgrade-success">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-gray-900 mb-4">
            Payment Successful!
          </h1>
          <p className="text-gray-600 mb-8">
            Your listing has been upgraded. You now have access to all premium features.
          </p>
          <div className="flex flex-col gap-4">
            <Link to="/dashboard">
              <Button className="w-full bg-teal-700 hover:bg-teal-800">
                Go to Dashboard
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/">
              <Button variant="outline" className="w-full">
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Error states
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4" data-testid="upgrade-error">
      <div className="text-center max-w-md">
        <h1 className="font-heading text-2xl font-bold text-gray-900 mb-4">
          {status === 'expired' ? 'Session Expired' : 'Something went wrong'}
        </h1>
        <p className="text-gray-600 mb-8">
          {status === 'expired'
            ? 'Your payment session has expired. Please try again.'
            : 'We couldn\'t verify your payment. Please contact support if you were charged.'}
        </p>
        <div className="flex flex-col gap-4">
          <Link to="/pricing">
            <Button className="w-full bg-teal-700 hover:bg-teal-800">
              Try Again
            </Button>
          </Link>
          <Link to="/dashboard">
            <Button variant="outline" className="w-full">
              Go to Dashboard
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default UpgradeSuccess;
